import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import clearForm from "@insite/client-framework/Store/Components/ContactUsForm/Handlers/ClearForm";
import setFieldValue from "@insite/client-framework/Store/Components/ContactUsForm/Handlers/SetFieldValue";
import validateReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ValidateReCaptcha";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ReCaptcha from "@insite/content-library/Components/ReCaptcha";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxProps } from "@insite/mobius/Checkbox";
import CheckboxGroup from "@insite/mobius/CheckboxGroup";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { getProp } from "@insite/mobius/utilities";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import submitLeadCaptureForm from "@tCentro/Handlers/CENSubmitLeadCaptureForm";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import React, { ReactNode, RefObject, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    formOverBanner = "formOverBanner",
    emailRecipients = "emailRecipients",
    submitButtonText = "submitButtonText",
    successMessage = "successMessage",
    heading = "heading",
    fullNameLabel = "fullNameLabel",
    emailAddressLabel = "emailAddressLabel",
    companyNameLabel = "companyNameLabel",
    zipCodeLabel = "zipCodeLabel",
    industryLabel = "industryLabel",
    topics = "topics",
    hintText = "hintText",
    checkboxText = "checkboxText",
    isRequired = "isRequired",
}

interface TopicModel {
    fields: {
        topic: string;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.formOverBanner]: boolean;
        [fields.emailRecipients]: string;
        [fields.submitButtonText]: string;
        [fields.successMessage]: string;
        [fields.heading]: string;
        [fields.fullNameLabel]: string;
        [fields.emailAddressLabel]: string;
        [fields.companyNameLabel]: string;
        [fields.zipCodeLabel]: string;
        [fields.industryLabel]: string;
        [fields.topics]: TopicModel[];
        [fields.hintText]: string;
        [fields.checkboxText]: string;
        [fields.isRequired]: boolean;
    };
}
const mapStateToProps = (state: ApplicationState) => ({
    fullName: state.components.contactUsForm.fieldValues["fullName"],
    emailAddress: state.components.contactUsForm.fieldValues["emailAddress"],
    companyName: state.components.contactUsForm.fieldValues["companyName"],
    zipCode: state.components.contactUsForm.fieldValues["zipCode"],
    topic: state.components.contactUsForm.fieldValues["topic"],
});

const mapDispatchToProps = {
    clearForm,
    validateReCaptcha: makeHandlerChainAwaitable<{ location: string; recaptchaRef: React.RefObject<ReCAPTCHA | null> }, boolean>(
        validateReCaptcha,
    ),
    setFieldValue,
    submitLeadCaptureForm,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface LeadCaptureFormStyles {
    container?: GridContainerProps;
    formGridItem?: GridItemProps;
    submitButton?: ButtonPresentationProps;
    successModal?: ModalPresentationProps;
    modalContainer?: GridContainerProps;
    messageTextGridItem?: GridItemProps;
    messageText?: TypographyPresentationProps;
    buttonGridItem?: GridItemProps;
    continueButton?: ButtonPresentationProps;
    heading?: InjectableCss;
    firstNameTextField?: TextFieldPresentationProps;
    input?: TextFieldPresentationProps;
    topicsSelect?: SelectPresentationProps;
    checkboxField?: CheckboxProps;
    formOverBanner?: InjectableCss;
}

export const leadCaptureFormStyles: LeadCaptureFormStyles = {
    formGridItem: {
        width: 12,
        css: css`
            padding: 0;
            padding-top: 65px !important;
            @media (min-width: 992px) and (max-width: 1199px) {
                margin-left: 30px !important;
            }
            @media (min-width: 1200px) {
                margin-left: 52px !important;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            padding: 16px !important;
                            padding-bottom: 0 !important;
                        `,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    submitButton: {
        css: css`
            float: left;
            margin-top: 26px;
            border-radius: 24px;
        `,
    },
    successModal: { sizeVariant: "small" },
    messageTextGridItem: { width: 12 },
    buttonGridItem: {
        width: 12,
        css: css`
            justify-content: flex-end;
        `,
    },
    heading: {
        css: css`
            font-size: ${TYPOGRAPHY.size.xxlarge};
            font-weight: ${TYPOGRAPHY.weight.bold};
            line-height: 40px;
            color: ${getProp("theme.typography.p.color")};
        `,
    },
    firstNameTextField: {
        cssOverrides: {
            formField: css`
                padding-top: 40px;
                label {
                    font-size: ${TYPOGRAPHY.size.smaller};
                    font-weight: ${TYPOGRAPHY.weight.mediumbold};
                    line-height: 16px;
                    color: ${getProp("theme.typography.p.color")};
                    margin-left: 24px;
                    min-height: auto !important;
                }
            `,
            formInputWrapper: css`
                margin-top: 8px !important;
                input {
                    background: ${COLORS.background.grayShade} !important;
                    border-radius: 40px !important;
                    padding: 12px 24px !important;
                    min-height: 48px !important;
                }
            `,
        },
    },
    input: {
        cssOverrides: {
            formField: css`
                padding-top: 24px;
                label {
                    font-size: ${TYPOGRAPHY.size.smaller};
                    font-weight: ${TYPOGRAPHY.weight.mediumbold};
                    line-height: 16px;
                    color: ${getProp("theme.typography.p.color")};
                    margin-left: 24px;
                    min-height: auto !important;
                }
            `,
            formInputWrapper: css`
                margin-top: 8px !important;
                input {
                    background: ${COLORS.background.grayShade} !important;
                    border-radius: 40px !important;
                    padding: 12px 24px !important;
                    min-height: 48px !important;
                }
            `,
        },
    },
    topicsSelect: {
        cssOverrides: {
            formField: css`
                padding-top: 24px;
                label {
                    font-size: ${TYPOGRAPHY.size.smaller};
                    font-weight: ${TYPOGRAPHY.weight.mediumbold};
                    line-height: 16px;
                    color: ${getProp("theme.typography.p.color")};
                    margin-left: 24px;
                    min-height: auto !important;
                }
            `,
            formInputWrapper: css`
                margin-top: 8px !important;
                select {
                    background: ${COLORS.background.grayShade} !important;
                    border-radius: 40px !important;
                    padding: 12px 24px !important;
                    min-height: 48px !important;
                }

                span[class^="IconWrapper"] {
                    background: url("/userFiles/images/chevronDown.svg") no-repeat;
                    height: 42px !important;
                    background-position: center;
                    svg {
                        display: none;
                    }
                }
            `,
        },
    },
    checkboxField: {
        css: css`
            padding-top: 12px;
            span[role="checkbox"] {
                border: 2px solid ${COLORS.black} !important;
                border-radius: 0 !important;
                &:focus {
                    box-shadow: none !important;
                }
                &[aria-checked="true"] {
                    background: none !important;
                    color: ${COLORS.black} !important;
                    border: 2px solid ${COLORS.black} !important;
                }
            }
            label {
                font-size: ${TYPOGRAPHY.size.smaller};
                line-height: 20px;
                color: ${getProp("theme.typography.p.color")};
            }
        `,
    },
    formOverBanner: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                            background: ${COLORS.white};
                            margin-top: -250px;
                            margin-left: 20px;
                            z-index: 9;
                            box-shadow: 0px 4px 8px rgb(38 37 37 / 24%);
                            padding: 32px;
                            div[class^="GridItemStyle"] {
                                margin-left: 0 !important;
                                padding-top: 0 !important;
                            }
                        `,
                        null,
                    ],
                    "min",
                )}
        `,
    },
};

const styles = leadCaptureFormStyles;
const emailsRegexp = new RegExp(
    "^[\\W]*([\\w+\\-.%]+@[\\w\\-.]+\\.[A-Za-z]{2,4}[\\W]*,{1}[\\W]*)*([\\w+\\-.%]+@[\\w\\-.]+\\.[A-Za-z]{2,4})[\\W]*$",
);
const emailRegexp = new RegExp("\\w+([-+.']\\w+)*@\\w+([-.]\\w+)*\\.(?!usa$)\\w+([-.]\\w+)*", "i");

type Validator = () => boolean;
type ContextType = {
    validators: {
        [key: string]: Validator | undefined;
    };
};

export const ContactFormContext = React.createContext<ContextType>({
    validators: {},
});

const LeadCaptureForm = ({
    id,
    submitLeadCaptureForm,
    fields,
    fullName,
    emailAddress,
    companyName,
    zipCode,
    topic,
    clearForm,
    setFieldValue,
}: Props) => {
    const {
        emailRecipients,
        submitButtonText,
        successMessage,
        heading,
        fullNameLabel,
        emailAddressLabel,
        companyNameLabel,
        zipCodeLabel,
        industryLabel,
        topics,
        hintText,
        checkboxText,
        isRequired,
    } = fields;
    const contactFormContext = React.useContext(ContactFormContext);
    const validators = contactFormContext.validators;
    const [fullNameErrorMessage, setFullNameErrorMessage] = React.useState("");
    const [emailAddressErrorMessage, setEmailAddressErrorMessage] = React.useState("");
    const [companyNameErrorMessage, setCompanyNameErrorMessage] = useState<ReactNode>("");
    const [zipCodeErrorMessage, setZipCodeErrorMessage] = React.useState("");
    const [industryErrorMessage, setTopicErrorMessage] = React.useState("");
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [optToSomething, setOptToSomething] = useState(false);

    const recaptchaRef: React.RefObject<ReCAPTCHA | null> = React.createRef();

    const formStyle = fields.formOverBanner ? { ...styles.formOverBanner } : null;

    const handleFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const widgetValidators: any[] = [];
        Object.keys(validators).forEach(key => {
            const validator = validators[key];
            if (validator) {
                widgetValidators.push(validator);
            }
        });
        const results = widgetValidators.map(v => v());
        if (!results.every(o => o)) {
            return;
        }

        const isReCaptchaValid = await validateReCaptcha({ location: "LeadCapture", recaptchaRef });
        if (!isReCaptchaValid) {
            return;
        }

        setIsSubmitting(true);
        if (
            emailAddress !== undefined &&
            companyName !== undefined &&
            fullName !== undefined &&
            zipCode !== undefined &&
            topic !== undefined
        ) {
            submitLeadCaptureForm({
                emailAddress,
                companyName,
                emailTo: emailAddress,
                fullName,
                zipCode,
                industry: topic,
                onSuccess: () => {
                    setIsModalOpen(true);
                    setIsSubmitting(false);
                },
                onComplete() {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onSuccess?.();
                },
                OptToSomethingKeyValPair: {
                    OptToSomethingLabel: fields.checkboxText,
                    OptToSomethingValue: optToSomething ? "Yes" : "No",
                },
            });
        }
    };

    React.useEffect(() => {
        contactFormContext.validators.fullName = validateFullName;
        contactFormContext.validators.emailAddress = validateEmailAddress;
        contactFormContext.validators.companyName = validateCompanyName;
        contactFormContext.validators.zipCode = validateZipCode;
        contactFormContext.validators.topic = validateIndustry;

        if (typeof fullName !== "undefined") {
            validateFullName();
        }

        if (typeof companyName !== "undefined") {
            validateCompanyName();
        }

        if (typeof zipCode !== "undefined") {
            validateZipCode();
        }

        if (typeof topic !== "undefined") {
            validateIndustry();
        }
    }, [fullName, emailAddress, companyName, zipCode, topic]);

    React.useEffect(() => {
        contactFormContext.validators.fullName = validateFullName;
        contactFormContext.validators.emailAddress = validateEmailAddress;
        contactFormContext.validators.companyName = validateCompanyName;
        contactFormContext.validators.zipCode = validateZipCode;
        contactFormContext.validators.topic = validateIndustry;
    }, [isRequired]);

    const validateFullName = () => {
        const errorMessage = isRequired && !fullName ? (siteMessage("Field_Required", fullNameLabel) as string) : "";
        setFullNameErrorMessage(errorMessage);
        return !errorMessage;
    };

    const validateEmailAddress = () => {
        const errorMessage =
            !emailAddress && isRequired
                ? siteMessage("ContactUsForm_EmailIsRequiredErrorMessage")
                : !emailAddress || emailRegexp.test(emailAddress)
                ? ""
                : siteMessage("ContactUsForm_EmailIsInvalidErrorMessage");
        setEmailAddressErrorMessage(errorMessage as string);
        return !errorMessage;
    };

    const validateCompanyName = () => {
        const errorMessage =
            isRequired && !companyName ? (siteMessage("Field_Required", companyNameLabel) as string) : "";
        setCompanyNameErrorMessage(errorMessage as string);
        return !errorMessage;
    };

    const validateZipCode = () => {
        const errorMessage = isRequired && !zipCode ? (siteMessage("Field_Required", zipCodeLabel) as string) : "";
        setZipCodeErrorMessage(errorMessage);
        return !errorMessage;
    };

    const validateIndustry = () => {
        const errorMessage = isRequired && !topic ? (siteMessage("Field_Required", industryLabel) as string) : "";
        setTopicErrorMessage(errorMessage);
        return !errorMessage;
    };

    const fullNameChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFieldValue({ key: "fullName", value: event.target.value });
    };

    const emailChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFieldValue({ key: "emailAddress", value: event.target.value });
    };

    const companyNameChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFieldValue({ key: "companyName", value: event.target.value });
    };

    const zipCodeChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFieldValue({ key: "zipCode", value: event.target.value });
    };

    const industryChangeHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setFieldValue({ key: "topic", value: event.target.value });
    };

    const emailBlurHandler = (event: React.FocusEvent<HTMLInputElement>) => {
        validateEmailAddress();
    };

    const modalCloseHandler = () => setIsModalOpen(false);

    const continueButtonClickHandler = () => {
        setIsModalOpen(false);
        clearForm();
    };

    return (
        <GridContainer {...styles.container}>
            <StyledWrapper {...formStyle}>
                <GridItem {...styles.formGridItem}>
                    <ContactFormContext.Provider value={{ validators }}>
                        <form id="leadCaptureForm" onSubmit={handleFormSubmit} noValidate>
                            <ReCaptcha recaptchaRef={recaptchaRef} location="LeadCapture" />
                            <Typography {...styles.heading}>{heading}</Typography>
                            <TextField
                                {...styles.firstNameTextField}
                                label={fullNameLabel}
                                required={isRequired}
                                value={fullName || ""}
                                error={fullNameErrorMessage}
                                onChange={fullNameChangeHandler}
                                onBlur={fullNameChangeHandler}
                            />
                            <TextField
                                {...styles.input}
                                label={emailAddressLabel}
                                required={isRequired}
                                value={emailAddress || ""}
                                error={emailAddressErrorMessage}
                                onChange={emailChangeHandler}
                                onBlur={emailBlurHandler}
                            />
                            <TextField
                                {...styles.input}
                                label={companyNameLabel}
                                required={isRequired}
                                value={companyName || ""}
                                error={companyNameErrorMessage}
                                onChange={companyNameChangeHandler}
                                onBlur={companyNameChangeHandler}
                            />
                            <TextField
                                {...styles.input}
                                label={zipCodeLabel}
                                required={isRequired}
                                value={zipCode || ""}
                                error={zipCodeErrorMessage}
                                onChange={zipCodeChangeHandler}
                                onBlur={zipCodeChangeHandler}
                            />
                            <Select
                                {...styles.topicsSelect}
                                label={industryLabel}
                                required={isRequired}
                                value={topic || ""}
                                error={industryErrorMessage}
                                onChange={industryChangeHandler}
                                onBlur={industryChangeHandler}
                            >
                                <option value="">{hintText}</option>
                                {topics &&
                                    topics.map(topic => (
                                        <option key={topic.fields.topic} value={topic.fields.topic}>
                                            {topic.fields.topic}
                                        </option>
                                    ))}
                            </Select>
                            <CheckboxGroup>
                                <Checkbox
                                    {...styles.checkboxField}
                                    checked={optToSomething}
                                    onChange={(event, value) => setOptToSomething(value)}
                                >
                                    {checkboxText}
                                </Checkbox>
                            </CheckboxGroup>

                            <Button {...styles.submitButton} type="submit" disabled={isSubmitting}>
                                {submitButtonText}
                            </Button>

                            <Modal
                                headline={translate("Message Sent")}
                                contentLabel={successMessage}
                                {...styles.successModal}
                                isOpen={isModalOpen}
                                handleClose={modalCloseHandler}
                            >
                                <GridContainer {...styles.modalContainer}>
                                    <GridItem {...styles.messageTextGridItem}>
                                        <Typography {...styles.messageText}>{successMessage}</Typography>
                                    </GridItem>
                                    <GridItem {...styles.buttonGridItem}>
                                        <Button {...styles.continueButton} onClick={continueButtonClickHandler}>
                                            {translate("Continue")}
                                        </Button>
                                    </GridItem>
                                </GridContainer>
                            </Modal>
                        </form>
                    </ContactFormContext.Provider>
                </GridItem>
            </StyledWrapper>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(LeadCaptureForm),
    definition: {
        group: "Contact Us",
        icon: "input-pipe",
        fieldDefinitions: [
            {
                name: fields.formOverBanner,
                displayName: "Display form over the banner image",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.emailRecipients,
                displayName: "Email recipients",
                editorTemplate: "MultilineTextField",
                fieldType: "General",
                defaultValue: "testaccount@insitesoft.com",
                isRequired: true,
                regularExpression: emailsRegexp,
            },
            {
                name: fields.heading,
                displayName: "Heading",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
            },
            {
                name: fields.fullNameLabel,
                displayName: "Full Name Label",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Full Name",
                isRequired: true,
            },
            {
                name: fields.emailAddressLabel,
                displayName: "Email Address Label",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Email Address",
                isRequired: true,
            },
            {
                name: fields.companyNameLabel,
                displayName: "Company Name Label",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Company Name",
                isRequired: true,
            },
            {
                name: fields.zipCodeLabel,
                displayName: "Zip Code Label",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Zip Code",
                isRequired: true,
            },
            {
                name: fields.industryLabel,
                displayName: "Industry Label",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Select an Industry",
                isRequired: true,
            },
            {
                name: fields.hintText,
                displayName: "Hint Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Select industry",
                isRequired: true,
            },
            {
                name: fields.topics,
                displayName: "Industries",
                editorTemplate: "ListField",
                defaultValue: [],
                getDisplay: (item: HasFields) => {
                    return item.fields.topic;
                },
                fieldType: "Translatable",
                fieldDefinitions: [
                    {
                        name: "topic",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                ],
            },
            {
                name: fields.isRequired,
                displayName: "Required Field",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.checkboxText,
                displayName: "Checkbox Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Checkbox to opt into something",
                isRequired: true,
            },
            {
                name: fields.submitButtonText,
                displayName: "Submit Button Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Send",
            },
            {
                name: fields.successMessage,
                displayName: "Submit Success Message",
                editorTemplate: "RichTextField",
                fieldType: "Translatable",
                defaultValue: "Thank you for your inquiry. A customer service representative will contact you shortly.",
            },
        ],
    },
};

export default widgetModule;
